import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import { ViewManagerBase } from '@blocksuite/data-view';
import { computed, signal } from '@preact/signals-core';

import type { DatabaseBlockDataSource } from './data-source.js';
import type { DatabaseUiStatePersistence } from './data-source-contracts.js';

export class DatabaseBlockViewManager extends ViewManagerBase {
  private readonly currentViewPersistence$ =
    signal<DatabaseUiStatePersistence>('durable');

  private readonly localCurrentViewId$ = signal<string | undefined>(undefined);

  private readonly transientCurrentViewId$ = signal<string | undefined>(
    undefined
  );

  override currentViewId$ = computed(() => {
    const transientViewId = this.transientCurrentViewId$.value;
    if (transientViewId && this.views$.value.includes(transientViewId)) {
      return transientViewId;
    }

    if (
      this.currentViewPersistence$.value === 'local' ||
      this.currentViewPersistence$.value === 'transient'
    ) {
      const localViewId = this.localCurrentViewId$.value;
      if (localViewId && this.views$.value.includes(localViewId)) {
        return localViewId;
      }
      return this.views$.value[0];
    }

    const activeViewId = this.model.props.activeViewId$.value;
    if (activeViewId && this.views$.value.includes(activeViewId)) {
      return activeViewId;
    }
    return this.views$.value[0];
  });

  constructor(
    dataSource: DatabaseBlockDataSource,
    private readonly model: DatabaseBlockModel
  ) {
    super(dataSource);
  }

  override setCurrentView(id: string | undefined): void {
    this.transientCurrentViewId$.value = undefined;
    const nextActiveViewId =
      id && this.views$.value.includes(id) ? id : undefined;
    if (
      this.currentViewPersistence$.value === 'local' ||
      this.currentViewPersistence$.value === 'transient'
    ) {
      if (this.localCurrentViewId$.value === nextActiveViewId) {
        return;
      }
      this.localCurrentViewId$.value = nextActiveViewId;
      if (this.currentViewPersistence$.value === 'local') {
        (this.dataSource as DatabaseBlockDataSource).persistLocalCurrentView(
          nextActiveViewId
        );
      }
      return;
    }
    if (this.model.props.activeViewId === nextActiveViewId) {
      return;
    }
    this.model.store.captureSync();
    this.model.store.transact(() => {
      this.model.props.activeViewId = nextActiveViewId;
    });
  }

  setTransientCurrentView(id: string | undefined): void {
    const nextViewId = id && this.views$.value.includes(id) ? id : undefined;
    if (this.transientCurrentViewId$.value === nextViewId) {
      return;
    }
    this.transientCurrentViewId$.value = nextViewId;
  }

  setCurrentViewPersistence(
    mode: DatabaseUiStatePersistence,
    initialLocalViewId?: string
  ): void {
    if (this.currentViewPersistence$.value === mode) {
      return;
    }
    this.transientCurrentViewId$.value = undefined;
    this.currentViewPersistence$.value = mode;
    this.localCurrentViewId$.value =
      (mode === 'local' || mode === 'transient') && initialLocalViewId
        ? initialLocalViewId
        : undefined;
  }
}
